import React, { useMemo, useState } from "react";
import { IconCheck, IconChevronLeft, IconSearch, IconSelector } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  searchEncoderCodesQuery,
  useEncoderCatalog,
  useEncoderCodeSearch,
  useEncoderInstructions,
} from "../../hooks/queries";
import type { CodeSearchResult } from "../../types/encoder";
import {
  cn,
  encoderDxTypeLabel,
  encoderErrorMessage,
  findAggregatedIcdCode,
  isEncoderDxType,
} from "../../utils/encoder";
import {
  CodeLabels,
  EncoderBrandEmpty,
  HighlightHtml,
  IcdInstructionCards,
  Spinner,
  useDebouncedValue,
} from "./shared";

const hasCptValue = (value: string | number | undefined): boolean =>
  value !== undefined && value !== null && String(value).trim().length > 0;

const formatGlobalPeriod = (value: string | number | undefined): string | null => {
  if (!hasCptValue(value)) return null;
  const raw = String(value).trim().toUpperCase();
  if (/^\d+$/.test(raw)) {
    const days = Number.parseInt(raw, 10);
    return days === 1 ? "1 day" : `${days} days`;
  }
  return raw;
};

const CodeDetailsPane: React.FC<{ code: CodeSearchResult }> = ({ code }) => {
  const cpt = code.metadata?.CPT;
  const pcs = code.metadata?.PCS;
  const isDx = isEncoderDxType(code.type);
  const isPcs = code.type === "PCS";
  const globalPeriod = formatGlobalPeriod(cpt?.DAYS ?? cpt?.GLOBAL_DAYS);
  const instructionsQuery = useEncoderInstructions([code.code], {
    enabled: isDx,
  });
  const icd = findAggregatedIcdCode(instructionsQuery.data, code.code);
  const catalog = useEncoderCatalog();
  const colors = catalog.data?.labelColors;

  return (
    <div className="grid h-full grid-cols-12 gap-4 p-4">
      <div className="col-span-10 flex h-full min-h-0 flex-col overflow-hidden">
        <div className="shrink-0 border-b pb-3">
          <h3 className="mb-1 text-xl font-semibold">
            <span className="font-mono">{code.code}</span>
            {" - "}
            <span>{code.description}</span>
          </h3>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pt-4">
          {isPcs && pcs?.Character_Definition && (
            <div className="space-y-4">
              {pcs.Character_Definition.filter(
                (charDef) =>
                  charDef.definition ||
                  charDef.explanation ||
                  (charDef.includes && charDef.includes.length > 0)
              ).map((charDef, index) => {
                const tones = [
                  "border-blue-200 bg-blue-50",
                  "border-emerald-200 bg-emerald-50",
                  "border-amber-200 bg-amber-50",
                ];
                return (
                  <div key={`${charDef.position}-${charDef.char}`}>
                    <h4 className="mb-2 text-sm font-semibold text-gray-900">
                      {charDef.label}
                    </h4>
                    <div className={cn("space-y-3 rounded-lg border p-4", tones[index % 3])}>
                      {charDef.definition && (
                        <div>
                          <p className="mb-1 text-xs font-medium text-slate-500">
                            Definition
                          </p>
                          <p className="text-sm text-gray-700">{charDef.definition}</p>
                        </div>
                      )}
                      {charDef.explanation && (
                        <div>
                          <p className="mb-1 text-xs font-medium text-slate-500">
                            Explanation
                          </p>
                          <p className="text-sm text-gray-700">{charDef.explanation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {isDx &&
            (instructionsQuery.isError ? (
              <p className="py-4 text-center text-sm text-red-600">
                {encoderErrorMessage(
                  instructionsQuery.error,
                  "Failed to load ICD-10 instructions"
                )}
              </p>
            ) : instructionsQuery.isLoading ? (
              <Spinner label="Loading instructions..." />
            ) : (
              <IcdInstructionCards data={icd} />
            ))}

          {cpt && (
            <div className="space-y-6">
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-2.5">
                <div className="flex flex-wrap items-center gap-4 text-xs">
                  {cpt.SI && (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-gray-500">SI:</span>
                        <span className="font-semibold text-gray-800">{cpt.SI}</span>
                      </div>
                      <div className="h-4 w-px bg-gray-300" />
                    </>
                  )}
                  {cpt.APC !== undefined && (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-gray-500">APC:</span>
                        <span className="font-semibold text-gray-800">{cpt.APC}</span>
                      </div>
                      <div className="h-4 w-px bg-gray-300" />
                    </>
                  )}
                  {cpt.MUE_UNIT !== undefined && (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-gray-500">MUE:</span>
                        <span className="font-semibold text-gray-800">{cpt.MUE_UNIT}</span>
                      </div>
                      <div className="h-4 w-px bg-gray-300" />
                    </>
                  )}
                  {hasCptValue(cpt.WORK_RVU) && (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-gray-500">Work RVU:</span>
                        <span className="font-semibold text-gray-800">{cpt.WORK_RVU}</span>
                      </div>
                      <div className="h-4 w-px bg-gray-300" />
                    </>
                  )}
                  {hasCptValue(cpt.FACILITY_RVU) && (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-gray-500">Facility RVU:</span>
                        <span className="font-semibold text-gray-800">{cpt.FACILITY_RVU}</span>
                      </div>
                      <div className="h-4 w-px bg-gray-300" />
                    </>
                  )}
                  {hasCptValue(cpt.NONFACILITY_RVU) && (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-gray-500">Non-Facility RVU:</span>
                        <span className="font-semibold text-gray-800">
                          {cpt.NONFACILITY_RVU}
                        </span>
                      </div>
                      {globalPeriod ? <div className="h-4 w-px bg-gray-300" /> : null}
                    </>
                  )}
                  {globalPeriod && (
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-gray-500">Global period:</span>
                      <span className="font-semibold text-gray-800">{globalPeriod}</span>
                    </div>
                  )}
                </div>
              </div>
              {cpt.GUIDELINES && (
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm whitespace-pre-wrap text-gray-700">
                  {cpt.GUIDELINES}
                </div>
              )}
              {cpt.MODIFIERS && cpt.MODIFIERS.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-red-100 text-left">
                      <th className="px-3 py-2">Code</th>
                      <th className="px-3 py-2">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cpt.MODIFIERS.map((modifier) => (
                      <tr key={modifier.code} className="border-b border-red-100">
                        <td className="px-3 py-2 font-mono font-semibold">
                          {modifier.code}
                        </td>
                        <td className="px-3 py-2">{modifier.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="col-span-2 flex flex-col gap-4 bg-gray-50 p-4">
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">Type</p>
          <p className="text-sm font-semibold">{encoderDxTypeLabel(code.type)}</p>
        </div>
        {hasCptValue(cpt?.FACILITY_RVU) && (
          <div>
            <p className="mb-1 text-xs font-medium text-slate-500">Facility RVU</p>
            <p className="text-sm font-semibold">{cpt?.FACILITY_RVU}</p>
          </div>
        )}
        {hasCptValue(cpt?.NONFACILITY_RVU) && (
          <div>
            <p className="mb-1 text-xs font-medium text-slate-500">Non-Facility RVU</p>
            <p className="text-sm font-semibold">{cpt?.NONFACILITY_RVU}</p>
          </div>
        )}
        {globalPeriod && (
          <div>
            <p className="mb-1 text-xs font-medium text-slate-500">Global period</p>
            <p className="text-sm font-semibold">{globalPeriod}</p>
            <p className="mt-1 text-xs text-slate-500">Post-operative care window</p>
          </div>
        )}
        {isPcs && pcs?.Character_Definition?.map((charDef) => (
          <div key={charDef.position} className="rounded-md border bg-white p-2.5">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                {charDef.name}
              </p>
              <span className="font-mono text-base font-bold">{charDef.char}</span>
            </div>
            <p className="text-sm text-gray-700">{charDef.label}</p>
          </div>
        ))}
        {(code.labels?.length ||
          code.metadata?.Rx?.v08 !== undefined ||
          code.metadata?.HCC?.v24 !== undefined ||
          code.metadata?.HCC?.v28 !== undefined) && (
          <div>
            <p className="mb-2 text-xs font-medium text-slate-500">Labels</p>
            <CodeLabels result={code} colors={colors} />
          </div>
        )}
      </div>
    </div>
  );
};

const CodeSearchTab: React.FC = () => {
  const queryClient = useQueryClient();
  const catalog = useEncoderCatalog();
  const years: number[] = catalog.data?.codeYears?.length
    ? catalog.data.codeYears
    : [2026, 2025, 2024, 2023, 2022];
  const colors = catalog.data?.labelColors;
  const [searchQuery, setSearchQuery] = useState("");
  const [year, setYear] = useState(years[0] ?? 2026);
  const [type, setType] = useState("All");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [selected, setSelected] = useState<CodeSearchResult | null>(null);
  const [pageResults, setPageResults] = useState<CodeSearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const debounced = useDebouncedValue(searchQuery, 500);
  const suggestions = useEncoderCodeSearch(debounced, type, year, {
    enabled: open && debounced.trim().length > 0,
  });
  const results: CodeSearchResult[] = suggestions.data ?? [];

  const runFullSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    try {
      const found = await searchEncoderCodesQuery({ q, type, year, limit: 20 });
      setPageResults(found);
      setSelected(null);
      setShowResults(true);
      setOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const choose = (code: CodeSearchResult, asPage = false) => {
    setSelected(code);
    setPageResults(asPage ? [code] : pageResults.length ? pageResults : [code]);
    setShowResults(true);
    setOpen(false);
    setSearchQuery("");
  };

  const types = useMemo(() => ["All", "Dx", "PCS", "CPT", "HCPCS"], []);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex min-w-0 items-center gap-4 border-b bg-white p-2 pb-2">
        <div className="relative min-w-0 flex-1">
          <div className="relative flex h-11 items-center overflow-hidden rounded-lg border border-slate-300 bg-transparent shadow-xs focus-within:border-slate-500 focus-within:ring-2 focus-within:ring-slate-300">
            <IconSearch
              size={16}
              className="pointer-events-none absolute left-3 text-slate-400"
            />
            <input
              value={searchQuery}
              placeholder="Search codes..."
              className="h-full min-w-0 flex-1 bg-transparent py-0 pl-9 pr-8 text-base outline-none placeholder:text-slate-400"
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setOpen(true);
                setHighlight(-1);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setHighlight((index) => Math.min(index + 1, results.length - 1));
                  return;
                }
                if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setHighlight((index) => Math.max(index - 1, 0));
                  return;
                }
                if (event.key === "Enter" && searchQuery.trim()) {
                  if (highlight >= 0 && results[highlight]) {
                    choose(results[highlight], true);
                    return;
                  }
                  event.preventDefault();
                  void runFullSearch();
                }
              }}
            />
            <IconSelector
              size={16}
              className="pointer-events-none absolute right-3 text-slate-400"
            />
          </div>
          {open && searchQuery.trim() && (
            <div className="absolute z-20 mt-1 flex h-[75vh] w-full flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
              <div className="min-h-0 flex-1 overflow-y-auto">
                {!debounced.trim() || searchQuery !== debounced ? null : suggestions.isFetching ? (
                  <Spinner label="Searching..." />
                ) : suggestions.isError ? (
                  <p className="px-3 py-4 text-sm text-red-600">
                    Error: {encoderErrorMessage(suggestions.error, "Failed to search codes")}
                  </p>
                ) : results.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-slate-500">No codes found</p>
                ) : (
                  results.map((code, index) => (
                    <button
                      key={`${code.type}-${code.code}`}
                      type="button"
                      onClick={() => choose(code, true)}
                      className={cn(
                        "flex w-full items-start gap-2 border-b border-gray-200 px-3 py-3 text-left",
                        index === highlight ? "bg-green-50" : "hover:bg-slate-50"
                      )}
                    >
                      <IconCheck
                        size={16}
                        className={cn(
                          "mt-1",
                          selected?.code === code.code ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2">
                          <span className="shrink-0 font-mono text-sm">{code.code}</span>
                          <span className="text-sm">{code.description}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="text-xs text-slate-500">{code.type}</span>
                          <CodeLabels result={code} colors={colors} />
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <span className="text-sm font-medium text-slate-500">Year:</span>
          <div className="inline-flex h-9 items-center rounded-md bg-gray-300 p-1">
            <select
              value={String(year)}
              onChange={(event) => {
                setYear(Number(event.target.value));
                setSelected(null);
                setPageResults([]);
                setShowResults(false);
              }}
              className="h-7 w-20 rounded-sm border-white bg-green-200 px-2 text-xs font-semibold text-green-700"
            >
              {years.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
          <span className="text-sm font-medium text-slate-500">Type:</span>
          <div className="inline-flex gap-1.5 rounded-md bg-gray-300 p-1">
            {types.map((item) => {
              const active = type === item;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setType(item);
                    setSelected(null);
                    const q = searchQuery.trim();
                    if (!q) {
                      setPageResults([]);
                      setShowResults(false);
                      return;
                    }
                    void queryClient
                      .fetchQuery({
                        queryKey: ["encoder", "codes", "search", { q, type: item, year, limit: 20 }],
                        queryFn: () =>
                          searchEncoderCodesQuery({ q, type: item, year, limit: 20 }),
                      })
                      .then((found: CodeSearchResult[]) => {
                        setPageResults(found);
                        setShowResults(true);
                      });
                  }}
                  className={cn(
                    "h-7 rounded-sm border px-4 text-xs",
                    active
                      ? "border-white bg-green-200 font-semibold text-green-700 shadow-lg"
                      : "border-transparent bg-gray-200 text-gray-600 hover:text-black"
                  )}
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {showResults && pageResults.length > 0 ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-2">
          {selected ? (
            <div className="flex h-full min-h-0 flex-col">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
              >
                <IconChevronLeft size={16} />
                Back to results
              </button>
              <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border bg-white">
                <CodeDetailsPane code={selected} />
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border bg-white">
              <p className="px-3 py-2 text-sm font-medium text-slate-500">
                Search Results ({pageResults.length})
              </p>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-gray-100/90">
                    <tr>
                      <th className="w-[120px] px-3 py-2 font-bold text-black">Code</th>
                      <th className="px-3 py-2 font-bold text-black">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageResults.map((code, index) => (
                      <tr
                        key={`${code.type}-${code.code}`}
                        onClick={() => setSelected(code)}
                        className={cn(
                          "cursor-pointer",
                          index % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50/50 hover:bg-gray-100"
                        )}
                      >
                        <td className="px-3 py-3 align-top font-mono text-base font-bold text-blue-900 underline">
                          {code.code}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="space-y-1.5">
                            <div className="text-base">
                              <HighlightHtml
                                html={code._formatted?.description}
                                fallback={code.description}
                              />
                            </div>
                            <CodeLabels result={code} colors={colors} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <EncoderBrandEmpty title="Encoder" />
      )}
    </div>
  );
};

export default CodeSearchTab;
